import axios from 'axios';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  blockWeekendPayout,
  createMarketplaceAccreditation,
  getWeekendPayoutStatus,
  listMarketplaceByIdi,
  unblockWeekendPayout,
  updateMarketplacePayment,
} from 'services/integratedPayment';
import { Marketplace } from 'shared/models/marketplace';
import { Driver } from 'shared/types/marketplace';
import { FeeKind } from 'shared/types/marketplacePayment';
import { useAppTabContext } from '../../AppContext';
import { buildAccreditationPayload } from './helpers/buildAccreditationPayload';
import { AccreditationFormValues } from './helpers/buildAccreditationPrefill';
import {
  buildUpdateFeesPayload,
  isValidFreePixFee,
  parseFreePixFeeValue,
} from './helpers/buildFreePixFeePayload';
import getPixDriver from './helpers/getPixDriver';
import {
  mapPixInfoFromMarketplace,
  PixInfoFromMarketplace,
} from './helpers/mapPixInfoFromMarketplace';

// lazy quebra o ciclo Context → IntegratedPayment → AccreditationDrawer → Context
const IntegratedPayment = lazy(() => import('./IntegratedPayment'));
export type WeekendPayoutStatus = 'Recebe' | 'Bloqueado' | 'Erro';

interface IntegratedPaymentContextData {
  marketplace: Marketplace | null;
  getDriver: Driver | null;
  loading: boolean;
  accreditationLoading: boolean;
  isRegistered: boolean;
  pixInfo: PixInfoFromMarketplace;
  weekendPayoutStatus: WeekendPayoutStatus | null;
  weekendPayoutLoading: boolean;
  setLoading(b: boolean): void;
  handleSubmit(args: {
    isPixEnabled: boolean;
    valor: string;
    tipo: FeeKind;
    oldValor: number;
    oldTipo: FeeKind;
  }): void;
  handleWeekendPayoutChange(nextReceives: boolean): void;
  handleAccreditationSubmit(
    form: AccreditationFormValues,
    onSuccess?: () => void,
  ): void;
  reloadMarketplace(): Promise<void>;
}

const Context = createContext<IntegratedPaymentContextData>(
  {} as IntegratedPaymentContextData,
);

export const useIntegratedPaymentContext = () => useContext(Context);

const IntegratedPaymentContext = () => {
  const {
    app,
    getDriver: appDriver,
    marketplace: appMarketplace,
  } = useAppTabContext();
  const { customer } = useCustomerContext();

  const [loading, setLoading] = useState(false);
  const [accreditationLoading, setAccreditationLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [marketplace, setMarketplace] = useState<Marketplace | null>(
    appMarketplace || null,
  );
  const [driver, setDriver] = useState<Driver | null>(appDriver);
  const [weekendPayoutStatus, setWeekendPayoutStatus] =
    useState<WeekendPayoutStatus | null>(null);
  const [weekendPayoutLoading, setWeekendPayoutLoading] = useState(false);

  const pixInfo = mapPixInfoFromMarketplace(
    marketplace?.drivers ? marketplace : null,
  );

  const loadWeekendPayout = useCallback(() => {
    if (!app?.idi) return;
    setWeekendPayoutLoading(true);
    getWeekendPayoutStatus(String(app.idi))
      .then((res) => {
        setWeekendPayoutStatus(
          res.data?.onWeekendPayoutBlocklist === true ? 'Bloqueado' : 'Recebe',
        );
      })
      .catch(() => {
        setWeekendPayoutStatus('Erro');
        toast.error('Falha ao carregar status do repasse FDS');
      })
      .finally(() => {
        setWeekendPayoutLoading(false);
      });
  }, [app?.idi]);

  const reloadMarketplace = useCallback(async () => {
    const res = await listMarketplaceByIdi(String(app.idi));
    const marketplaceData = new Marketplace(res.data);
    setMarketplace(marketplaceData);
    setIsRegistered(true);
    setDriver(getPixDriver(marketplaceData.drivers));
  }, [app.idi]);

  const markUnregistered = useCallback(() => {
    setIsRegistered(false);
    setMarketplace(null);
    setDriver(null);
  }, []);

  const handleSubmit = ({
    isPixEnabled,
    valor,
    tipo,
    oldValor,
    oldTipo,
  }: {
    isPixEnabled: boolean;
    valor: string;
    tipo: FeeKind;
    oldValor: number;
    oldTipo: FeeKind;
  }) => {
    if (!isPixEnabled) {
      toast.error('Necessário ativar o toogle!');
      return;
    }

    if (!isValidFreePixFee(valor, tipo)) {
      toast.error(
        'Informe um valor numérico e o tipo da taxa (porcentagem ou fixo)',
      );
      return;
    }

    setLoading(true);
    const payload = buildUpdateFeesPayload({
      customerId: customer.id,
      idi: app.idi,
      valor: parseFreePixFeeValue(valor),
      tipo,
      oldValor,
      oldTipo,
    });

    updateMarketplacePayment(payload)
      .then(() => {
        toast.success('Dados atualizados com sucesso!');
        return reloadMarketplace();
      })
      .catch((error) => {
        console.error('Falha ao atualizar dados de pagamento', error);
        toast.error('Falha ao atualizar dados de pagamento!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAccreditationSubmit = (
    form: AccreditationFormValues,
    onSuccess?: () => void,
  ) => {
    setAccreditationLoading(true);
    const payload = buildAccreditationPayload({
      form,
      customerId: customer.id,
      idi: app.idi,
    });

    createMarketplaceAccreditation(payload)
      .then(() => {
        toast.success('Credenciamento PIX realizado com sucesso');
        onSuccess?.();
        return reloadMarketplace().then(() => {
          loadWeekendPayout();
        });
      })
      .catch((error) => {
        console.error('Falha ao credenciar PIX', error);
        toast.error('Falha ao credenciar PIX');
      })
      .finally(() => {
        setAccreditationLoading(false);
      });
  };

  const handleWeekendPayoutChange = (nextReceives: boolean) => {
    const previous = weekendPayoutStatus;
    setWeekendPayoutLoading(true);
    setWeekendPayoutStatus(nextReceives ? 'Recebe' : 'Bloqueado');

    const request = nextReceives
      ? unblockWeekendPayout(String(app.idi), customer.id)
      : blockWeekendPayout(String(app.idi), customer.id);

    request
      .then(() => {
        toast.success(
          nextReceives
            ? 'Repasse FDS atualizado: Recebe'
            : 'Repasse FDS atualizado: Bloqueado',
        );
        return getWeekendPayoutStatus(String(app.idi)).then((res) => {
          setWeekendPayoutStatus(
            res.data?.onWeekendPayoutBlocklist === true
              ? 'Bloqueado'
              : 'Recebe',
          );
        });
      })
      .catch(() => {
        toast.error('Falha ao atualizar repasse FDS');
        if (previous === 'Recebe' || previous === 'Bloqueado') {
          setWeekendPayoutStatus(previous);
        } else {
          loadWeekendPayout();
        }
      })
      .finally(() => {
        setWeekendPayoutLoading(false);
      });
  };

  useEffect(() => {
    if (appMarketplace && appMarketplace.drivers) {
      setMarketplace(appMarketplace);
      setLoading(false);
      setIsRegistered(true);
      loadWeekendPayout();
    } else {
      listMarketplaceByIdi(String(app.idi))
        .then((res) => {
          const marketplaceData = new Marketplace(res.data);
          setMarketplace(marketplaceData);
          setIsRegistered(true);
          loadWeekendPayout();
        })
        .catch((error) => {
          const status = axios.isAxiosError(error)
            ? error.response?.status
            : undefined;

          if (status !== 404 && status !== 422) {
            console.error('Falha ao carregar o marketplace', error);
          }

          markUnregistered();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [app.idi, appMarketplace, loadWeekendPayout, markUnregistered]);

  useEffect(() => {
    if (appDriver) {
      setDriver(appDriver);
    } else if (marketplace?.drivers) {
      setDriver(getPixDriver(marketplace.drivers));
    }
  }, [appDriver, marketplace?.drivers]);

  const data: IntegratedPaymentContextData = {
    marketplace,
    getDriver: driver,
    loading,
    accreditationLoading,
    isRegistered,
    pixInfo,
    weekendPayoutStatus,
    weekendPayoutLoading,
    setLoading,
    handleSubmit,
    handleWeekendPayoutChange,
    handleAccreditationSubmit,
    reloadMarketplace,
  };

  return (
    <Context.Provider value={data}>
      <Suspense fallback={null}>
        <IntegratedPayment />
      </Suspense>
    </Context.Provider>
  );
};

export default IntegratedPaymentContext;
